import {useContext, useEffect, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {CustomWorldListItem as BaseCustomWorldListItem} from "../lists/CustomWorldListItem";
import {
  CONTEXT_ACTION_TYPE,
  CustomWorldPayload,
  DataStoreContext,
  STEP_ORDERS,
  STEPS,
  useStep,
  validateStepOutput
} from "../../contexts/OperatingTestContext";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import Pjdb1TextField from "../../../../components/inputs/Pjdb1TextField";
import {FetchMessage} from "../../../../hooks/useFetchAllQuery";
import {useCustomWorldByBaseWorld} from "../../hooks/useCustomWorld";
import {CustomWorldDocument} from "../../../../types/pj1dbApiWorld";
import {WorldDetailDialog} from "../details/WorldDetailDialog";

export default function CustomWorldSelectionStep() {
  if (!useIsShow()) {
    throw new Error('CustomWorldSelectionStep is not shown');
  }

  const {state, dispatch} = useContext(DataStoreContext);
  const [, setStep] = useStep();
  const [timer, setTimer] = useState<NodeJS.Timeout | null>();
  const {fetchAll} = useCustomWorldByBaseWorld({
    baseWorldIds: [state.baseWorldStep.selectedBaseWorld?.attr["world-id"] ?? ''],
    searchText: state.customWorldStep.searchText,
    onChange: (newData, newStatus) => {
      console.log(newData, newStatus);
      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD,
        payload: {customWorlds: newData, searchStatus: newStatus} satisfies CustomWorldPayload
      });
    },
  });

  useEffect(() => {
    if (timer) {
      clearTimeout(timer);
    }

    const t = setTimeout(() => {
      fetchAll().catch(console.error);
    }, 500);
    setTimer(t);

    return () => {
      if (t) {
        clearTimeout(t);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.customWorldStep.searchText]);

  const customWorlds = state.customWorldStep.customWorlds.map(item => Object.values(item)).flat();

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant="h6" component='h2'>
            ベースワールド名：{state.baseWorldStep.selectedBaseWorld?.attr['name-jp']}
          </Typography>
          <Pjdb1Button
            label='ベースワールド選択'
            variant='text'
            color='inherit'
            onClick={() => {
              setStep(STEP_ORDERS[STEPS.CUSTOM_WORLD_SELECTION].PREV);
            }}
          />
        </Box>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant="body2" sx={{fontWeight: "bold"}}>
            カスタムワールドを選択してください。
          </Typography>

          <Box component="span">
            {customWorlds.length > 0 ? `${customWorlds.length.toLocaleString()}件` : ""}
          </Box>
        </Box>
        <Pjdb1TextField
          label='カスタムワールド名などを入力'
          required={false}
          sx={{bgcolor: 'white', width: {xs: '100%', sm: '50%'}}}
          onChange={(e) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD,
              payload: {
                searchText: e.target.value,
              } satisfies CustomWorldPayload
            });
          }}
          name="customWorldSearchText"
          value={state.customWorldStep.searchText}
        />
      </Stack>

      {customWorlds.length > 0 && (
        <Box display='flex' flexWrap='wrap' gap={4}>
          {customWorlds.map(item => (
            <CustomWorldListItem
              key={item.attr['world-id']}
              customWorld={item}
              onClick={(customWorld) => {
                dispatch({
                  type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD,
                  payload: {selectedCustomWorld: customWorld} satisfies CustomWorldPayload
                });
                setStep(STEP_ORDERS[STEPS.CUSTOM_WORLD_SELECTION].NEXT);
              }}
            />
          ))}
        </Box>
      )}

      <FetchMessage
        data={state.customWorldStep.customWorlds}
        status={state.customWorldStep.searchStatus}
        isEmpty={
          (value) => value.length === 0
            || value.every(item => Object.values(item).length === 0)
        }
      />
    </Stack>
  );
}

function useIsShow() {
  const {state} = useContext(DataStoreContext);
  const [step] = useStep();

  return step === STEPS.CUSTOM_WORLD_SELECTION &&
    validateStepOutput(state, STEP_ORDERS[STEPS.CUSTOM_WORLD_SELECTION].PREV);
}

function CustomWorldListItem(props: {
  customWorld: CustomWorldDocument;
  onClick: (customWorld: CustomWorldDocument) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <WorldDetailDialog
        open={open}
        worldId={props.customWorld.attr["world-id"]}
        onClose={() => setOpen(false)}
      />
      <BaseCustomWorldListItem item={props.customWorld} onClick={() => setOpen(true)}>
        <Pjdb1Button label="動作試験環境を決定する" variant='contained' color='info' onClick={(e) => {
          e.stopPropagation();
          props.onClick(props.customWorld);
        }}/>
      </BaseCustomWorldListItem>
    </>
  )
}
